import { memo, type Ref, useImperativeHandle, useRef } from "react";
import ProductEntry from "./ProductEntry";
import { type CartGrouping } from "../../../../models/CartData";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import {
  makeObserveWindowOffset,
  makeWindowScrollOffset,
} from "~/Cart/components/CartPage/ProductTable/ProductEntryList/TanstackVirtualUtils";

export interface ProductEntryListRef {
  scrollToIndex: (index: number) => void;
}

interface ListProps {
  group: CartGrouping;
  editMode?: boolean;

  scrollRef?: Ref<ProductEntryListRef>;
}

const RegularProductEntryList = ({ group, editMode, scrollRef }: ListProps) => {
  const virtualListContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useWindowVirtualizer({
    count: group.entries.length,
    estimateSize: (index) =>
      Math.max(460, 188 + group.entries[index].packGroups.flatMap((e) => e.packEntryIds).length * 245),
    overscan: 8,
    // @ts-ignore
    observeElementOffset: makeObserveWindowOffset(virtualListContainerRef),
    // @ts-ignore
    scrollToFn: makeWindowScrollOffset(virtualListContainerRef),
    scrollPaddingEnd: 200,
    scrollPaddingStart: 200,
    shouldAdjustScrollPositionOnItemSizeChange: () => true,
  });

  useImperativeHandle(scrollRef, () => ({
    scrollToIndex: (index) => {
      rowVirtualizer.scrollToIndex(index);
      virtualListContainerRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({
        block: "center",
      });
    },
  }));

  return (
    <div
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: "100%",
        position: "relative",
      }}
      ref={virtualListContainerRef}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => (
        <div
          key={virtualRow.index}
          className={!editMode ? "scha__cart__product-entry--with-spacer" : ""}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualRow.start}px)`,
          }}
        >
          <ProductEntry
            key={virtualRow.index}
            index={virtualRow.index}
            groupingEntry={group.entries[virtualRow.index]}
            editMode={editMode}
            onRender={rowVirtualizer.measureElement}
          />
        </div>
      ))}
    </div>
  );
};

const ProductEntryList = RegularProductEntryList;

export default memo(ProductEntryList);
